/**
 * 콘텐츠 게시 기간 — 입력 칸과 사람이 읽는 상태.
 *
 * 서버는 절대 시각(ISO)으로 받고 돌려준다. 사람은 **콘솔을 연 컴퓨터의 시각**으로 적고 읽는다
 * (`datetime-local`). 둘 사이를 오가는 변환을 한 곳에 둔다 — 화면마다 따로 바꾸면 한쪽만 시간대가
 * 어긋난다.
 */

/** ISO → `datetime-local` 값(`YYYY-MM-DDTHH:mm`, 이 컴퓨터의 시각). 없으면 빈 문자열. */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}T${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

/** `datetime-local` 값 → ISO. 빈 칸이면 null(그쪽 끝 제한 없음). 읽을 수 없으면 undefined. */
export function fromLocalInput(value: string): string | null | undefined {
  if (!value.trim()) return null;
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? undefined : at.toISOString();
}

/** 두 칸을 함께 읽는다 — 순서가 틀리면 사람이 읽을 이유를 준다. */
export function readValidity(
  from: string,
  until: string,
): { valid_from: string | null; valid_until: string | null } | { error: string } {
  const validFrom = fromLocalInput(from);
  const validUntil = fromLocalInput(until);
  if (validFrom === undefined) return { error: '게시 시작을 날짜로 읽을 수 없습니다' };
  if (validUntil === undefined) return { error: '게시 종료를 날짜로 읽을 수 없습니다' };
  if (validFrom && validUntil && Date.parse(validFrom) >= Date.parse(validUntil)) {
    return { error: '게시 종료는 시작보다 뒤여야 합니다' };
  }
  return { valid_from: validFrom, valid_until: validUntil };
}

export interface ValidityBadge {
  label: string;
  tone: 'warn' | 'danger' | 'neutral';
  /** 마우스를 올리면 보이는 정확한 시각. */
  title: string;
}

const DAY_MS = 86_400_000;

function when(iso: string): string {
  return new Date(iso).toLocaleString('ko-KR', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/**
 * 목록에 붙일 게시 상태. 기간이 없거나 한참 남았으면 null — 늘 붙어 있는 배지는 아무도 읽지 않는다.
 *
 * - 끝났다 → `게시 종료`(빨강) — 편성에 있어도 나가지 않는다
 * - 7일 안에 끝난다 → `D-3 종료` · 오늘이면 `오늘 18:00 종료`(주황)
 * - 아직 시작 전 → `6. 16. 09:00 시작`(회색)
 */
export function validityBadge(
  asset: { valid_from?: string | null; valid_until?: string | null },
  now: Date = new Date(),
): ValidityBadge | null {
  const t = now.getTime();
  if (asset.valid_until) {
    const end = Date.parse(asset.valid_until);
    if (end <= t) return { label: '게시 종료', tone: 'danger', title: `${when(asset.valid_until)}에 게시가 끝나 화면에 나가지 않습니다` };
    if (end - t < DAY_MS && new Date(end).getDate() === now.getDate()) {
      return {
        label: `오늘 ${new Date(end).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })} 종료`,
        tone: 'warn',
        title: `${when(asset.valid_until)}에 게시가 끝납니다`,
      };
    }
    if (end - t <= 7 * DAY_MS) {
      return {
        label: `D-${Math.ceil((end - t) / DAY_MS)} 종료`,
        tone: 'warn',
        title: `${when(asset.valid_until)}에 게시가 끝납니다`,
      };
    }
  }
  if (asset.valid_from && Date.parse(asset.valid_from) > t) {
    return { label: `${when(asset.valid_from)} 시작`, tone: 'neutral', title: '게시 시작 전이라 아직 화면에 나가지 않습니다' };
  }
  return null;
}
